import {
  extractMediaFileName,
  fetchMediaAsObjectUrl,
} from "Helpers/mediaView";

/** Shared blob URL cache keyed by sanitized image file name. */
const imageCache = new Map<string, string>();

/** In-flight requests so concurrent callers share one fetch. */
const inflight = new Map<string, Promise<string>>();

export const normalizeMediaImageKey = (urlOrName: string): string => {
  if (!urlOrName) return "";
  if (
    urlOrName.startsWith("data:") ||
    urlOrName.startsWith("blob:") ||
    urlOrName.startsWith("http://") ||
    urlOrName.startsWith("https://")
  ) {
    return urlOrName;
  }
  return extractMediaFileName(urlOrName);
};

export const getCachedMediaObjectUrl = (urlOrName: string): string | null => {
  const key = normalizeMediaImageKey(urlOrName);
  if (!key) return null;
  return imageCache.get(key) ?? null;
};

/**
 * Resolve a media file to a displayable blob/data/http URL.
 * Uses a shared cache + in-flight dedupe across the app.
 */
export async function resolveMediaObjectUrl(
  urlOrName: string,
): Promise<string> {
  const key = normalizeMediaImageKey(urlOrName);
  if (!key) return "";

  const cached = imageCache.get(key);
  if (cached) return cached;

  const pending = inflight.get(key);
  if (pending) return pending;

  const request = (async () => {
    // Already displayable absolute/data/blob URLs — cache as-is (no API).
    if (
      key.startsWith("data:") ||
      key.startsWith("blob:") ||
      key.startsWith("http://") ||
      key.startsWith("https://")
    ) {
      imageCache.set(key, key);
      return key;
    }

    const objectUrl = await fetchMediaAsObjectUrl(key);
    if (objectUrl) {
      imageCache.set(key, objectUrl);
      return objectUrl;
    }
    return "";
  })().finally(() => {
    inflight.delete(key);
  });

  inflight.set(key, request);
  return request;
}

/** Prefetch without throwing; respects cache + inflight. */
export function prefetchMediaObjectUrl(urlOrName: string): void {
  const key = normalizeMediaImageKey(urlOrName);
  if (!key) return;
  if (imageCache.has(key) || inflight.has(key)) return;
  void resolveMediaObjectUrl(key);
}

/**
 * Revoke blob URLs and clear caches. Call when the app no longer needs
 * cached inspection/media images (e.g. leaving the page).
 */
export function clearMediaImageCache(): void {
  imageCache.forEach((url) => {
    if (url?.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* ignore */
      }
    }
  });
  imageCache.clear();
  inflight.clear();
}
